/**
 * genericPages.js — page types that work in EVERY template, with no per-page code.
 *
 * Deepak's model (ADR 0015): the team picks a type of page, makes that page, and
 * stitches pages into the report. Every page of the team's 12 templates is one
 * of four layouts — text, a six-view grid, a single view, a set of print models —
 * so a handful of generic types covers them, and the operator supplies what
 * differs from case to case: which meshes the page shows, which view, and the
 * captions.
 *
 *   Six views      the meshes the operator picks, from the six standard views
 *   Single view    the meshes the operator picks, from one view, filling the page
 *   Print models   one render per mesh the operator picks, three to a sheet
 *   CT data        the scan's data, read from the portal by Case ID
 *   Agreement, Terms and Conditions (1 of 2), (2 of 2) — the shared text
 *
 * Captions are NOT an input here: they are the page's edits, keyed to the page
 * instance like every other caption (edits.js), so there is one place a caption
 * lives whichever control the operator types it in.
 *
 * Ids carry a `g-` prefix so they can never collide with a template's own
 * recipe ids, which fill the template pages that use them (formats.js).
 *
 * Each page frames itself: generic pages have no fit group, so a page's
 * pictures are sized for that page alone. Every page says "Images are not to
 * scale".
 */
import {
  newGridPage, newGridCell, newGalleryPage, newInfoPage, newTextPage, GRID_VIEWS,
  viewRowPage, FOUR_VIEWS, waiverBlockFor,
} from './schema.js'
import { ROLE_LABELS } from './classify.js'
import { reconstructionSide } from './fibulaRecipes.js'
import { CT_FIELDS, ctSubheading } from '../sseReport.js'
import {
  AGREEMENT_BODY, TERMS_BODY_1, TERMS_BODY_2, AGREEMENT_STYLES, TERMS_STYLES,
  TI_PSI_AGREEMENT_BODY, TI_PSI_TERMS_BODY_1,
} from './boilerplate.js'

/** What a generic page captions itself when its format states nothing. */
const NO_CAPTIONS = { tc: '', bc: '' }

/** The views a single-view page can show — the six standard ones and the isometric. */
export const SINGLE_VIEWS = [...GRID_VIEWS, 'ISO']

/**
 * The colour-map legend as typed, one row per line: a colour then its label —
 * `#ff0000 0.10 mm`, `255,0,0 0.10 mm` or `red 0.10 mm` (the six CSS names the
 * team's ZBrush ramps use). Rows without a readable colour are dropped, so a
 * half-typed line never draws a black swatch. An array is passed through.
 */
const NAMED = {
  red: [255, 0, 0], green: [0, 200, 0], blue: [0, 0, 255],
  yellow: [255, 220, 0], cyan: [0, 200, 220], magenta: [220, 0, 220],
}
/**
 * How a gallery of `n` renders is paged, as the delivered reports do it: up to SIX on a page,
 * one column for one or two (FIBULA p20/p21), two columns from three up (ORTHO p15, five
 * models). Pages are filled evenly, so seven models make 4 + 3, not 6 + 1.
 */
export function galleryPaging(n) {
  const pageCount = Math.max(1, Math.ceil(n / 6))
  const perPage = Math.ceil(n / pageCount)
  return { perPage, cols: perPage >= 3 ? 2 : 1 }
}

export function parseLegend(input) {
  if (Array.isArray(input)) return input.filter((r) => r && Array.isArray(r.color) && r.label)
  if (typeof input !== 'string') return []
  const rows = []
  for (const raw of input.split(/\r?\n/)) {
    const line = raw.trim()
    if (!line) continue
    const m = line.match(/^(#[0-9a-f]{6}|\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}|[a-z]+)\s+(.+)$/i)
    if (!m) continue
    const [, c, label] = m
    let color = null
    if (c.startsWith('#')) color = [1, 3, 5].map((i) => parseInt(c.slice(i, i + 2), 16))
    else if (c.includes(',')) color = c.split(',').map((n) => Math.min(255, parseInt(n.trim(), 10)))
    else color = NAMED[c.toLowerCase()] ?? null
    if (color) rows.push({ color, label: label.trim() })
  }
  return rows
}

/**
 * The meshes this page's operator picked, as library entries. Ids no longer in
 * the library are counted, so the page can say a chosen mesh has gone rather
 * than quietly showing less.
 */
function chosenMeshes(ctx) {
  const ids = ctx.entry?.inputs?.meshes ?? []
  const found = ids.map((id) => ctx.library.find((m) => m.id === id)).filter(Boolean)
  return { found, gone: ids.length - found.length }
}

function note(ctx, kind, reason) {
  ctx.notes.push({ page: ctx.recipe.title, kind, reason, missing: [] })
}

/**
 * Words in a filename that tell two models of the same kind apart. A caption
 * is never the filename itself: real case files carry the patient's name.
 */
const QUALIFIERS = ['Anterior', 'Posterior', 'Left', 'Right', 'Upper', 'Lower', 'Proximal', 'Distal']
const qualifierOf = (name) => QUALIFIERS.find((w) => new RegExp(`\\b${w}\\b`, 'i').test(name)) ?? null

/**
 * A file whose name says it holds anatomy AND a fabricated part — "Post plan skull with
 * splint.OBJ". One file has one appearance, so the splint in it prints in the skull's tone
 * (the team's trial report, 28 Sep: "splint colour needs to be white"). The portal cannot
 * tell the splint's triangles from the skull's in an unnamed polygroup; the file that renders
 * right is the splint on its own. Said on the page row, so the operator knows what to do.
 */
// Only the two words whose own file prints white (classify.js: splint*, guide*). A "plate" or a
// "wafer" has no role of its own and would print bone on its own too, so no promise is made.
const COMBINED_FILE = /\bwith\s+(a\s+|the\s+)?(splint|guide)s?\b/i
export const combinedFileAdvice = (meshes) => meshes
  .filter((m) => COMBINED_FILE.test(m.name))
  .map((m) => {
    const part = COMBINED_FILE.exec(m.name)[2].toLowerCase()
    return `"${m.name}" holds the anatomy and the ${part} in one file, so the ${part} prints in the `
      + `anatomy's colour. Upload the ${part} as its own file — it prints white — or set its polygroup under Part types`
  })

/** The picked meshes, or null after saying why the page cannot be built. */
function needMeshes(ctx) {
  const { found, gone } = chosenMeshes(ctx)
  // Said once per document, on the first page that picks the file — not under every page.
  for (const advice of combinedFileAdvice(found)) {
    if (!ctx.notes.some((n) => n.reason === advice)) note(ctx, 'incomplete', advice)
  }
  if (gone) {
    note(ctx, found.length ? 'incomplete' : 'skipped',
      `${found.length ? 'built WITHOUT' : 'not built —'} ${gone} chosen mesh${gone === 1 ? '' : 'es'} `
      + 'no longer in the case folder — choose again')
  }
  if (!found.length) {
    if (!gone) note(ctx, 'skipped', 'not built — choose the meshes this page shows')
    return null
  }
  return found
}

/**
 * `formatOnly` keeps a page type OUT of the generic "Add a page" list while leaving it
 * resolvable by id, which is how a format's own page reaches it through `use:` (pageTypes.js
 * `pageType` tries `genericPage(id)` first). Without it the Titanium PSI agreement would be
 * offered on every report beside the house one, two entries both reading "Agreement" — and one
 * name for one thing is the rule these screens are held to.
 */
const text = (id, title, body, styles, { formatOnly = false } = {}) => ({
  id, title, kind: 'custom', block: 'text', repeatable: false, inputs: [], formatOnly,
  compose(ctx) {
    const page = newTextPage(ctx.nextId('p'), body)
    page.styles = styles
    page.recipeId = ctx.recipe.id
    ctx.doc.pages.push(page)
  },
})

export const GENERIC_PAGES = [
  {
    id: 'g-six-views', title: 'Six views', kind: 'custom', block: 'grid', repeatable: true,
    inputs: ['meshes'],
    compose(ctx) {
      const meshes = needMeshes(ctx)
      if (!meshes) return
      const parts = meshes.map(ctx.partFor)
      const sceneId = ctx.addScene('Six views', parts, undefined, ctx.impliedFrame(parts))
      // A template page's own captions, when this generic page is one (formats.js).
      // Plain "Six views" has none, and the operator types them.
      ctx.pushSixViewPage(sceneId, { ...NO_CAPTIONS, ...(ctx.recipe.captions ?? {}) }, ctx.recipe.id)
    },
  },
  {
    // A ROW of chosen views of one object, in one frame — the shape the fibula
    // template uses for its leg pages. The format states the views; four is the
    // default because the native-leg page is the one that stated none.
    //
    // `LAT` is the lateral view of the harvested leg: the template carries BOTH
    // badges on those pages with one hidden, beside a "Left leg" / "Right leg"
    // caption with one hidden. Measured in the reference dump — Page 10 and
    // Page 11 show A, LL, P with RL hidden. Resolved here from the case's own
    // files, the same source the laterality sentence uses.
    id: 'g-view-row', title: 'Row of views', kind: 'custom', block: 'grid', repeatable: true,
    inputs: ['meshes'],
    compose(ctx) {
      const meshes = needMeshes(ctx)
      if (!meshes) return
      const parts = meshes.map(ctx.partFor)
      const sceneId = ctx.addScene('Row of views', parts, undefined, ctx.impliedFrame(parts))
      const stated = ctx.recipe.views ?? FOUR_VIEWS
      const side = reconstructionSide(ctx.caseLibrary ?? ctx.library ?? [])
      // A page that asks for the lateral and cannot be told which leg says so. The
      // template's own default is the left one, so that is what is drawn — but
      // drawing it silently on a right-leg case prints the wrong side of the
      // patient, which is the one thing the laterality sentence refuses to do.
      if (stated.includes('LAT') && !side.side) {
        note(ctx, 'incomplete', side.conflict
          ? `built with the LEFT lateral view — the files disagree about which leg: ${side.from.join(', ')}. `
            + 'Check which leg, then set the view on this page.'
          : 'built with the LEFT lateral view — no filename says which leg. '
            + 'Check which leg, then set the view on this page.')
      }
      const views = stated.map((v) => (v === 'LAT' ? (side.side === 'right' ? 'RL' : 'LL') : v))
      const page = viewRowPage(ctx.nextId('p'), sceneId, views)
      page.captions = { ...page.captions, ...NO_CAPTIONS, ...(ctx.recipe.captions ?? {}) }
      page.recipeId = ctx.recipe.id
      ctx.doc.pages.push(page)
    },
  },
  {
    id: 'g-single-view', title: 'Single view', kind: 'custom', block: 'grid', repeatable: true,
    inputs: ['meshes', 'view'],
    compose(ctx) {
      const meshes = needMeshes(ctx)
      if (!meshes) return
      const chosen = ctx.entry?.inputs?.view ?? ctx.recipe.view
      const view = SINGLE_VIEWS.includes(chosen) ? chosen : 'A'
      const parts = meshes.map(ctx.partFor)
      const sceneId = ctx.addScene('Single view', parts, undefined, ctx.impliedFrame(parts))
      const page = newGridPage(ctx.nextId('p'), {
        rows: 1, cols: 1, badge: 'perCell', cells: [newGridCell(sceneId, view)],
      })
      page.captions = { ...page.captions, ...NO_CAPTIONS, ...(ctx.recipe.captions ?? {}) }
      page.recipeId = ctx.recipe.id
      ctx.doc.pages.push(page)
    },
  },
  {
    // One render per chosen mesh, captioned with what it is, three to a sheet —
    // balanced, so four models split 2 + 2 rather than 3 + 1, as the
    // orthognathic print-models page already does.
    id: 'g-print-models', title: 'Print models', kind: 'custom', block: 'gallery', repeatable: true,
    inputs: ['meshes'],
    compose(ctx) {
      const meshes = needMeshes(ctx)
      if (!meshes) return
      const base = (m) => ROLE_LABELS[m.role] ?? m.role
      const shared = (m) => meshes.filter((x) => base(x) === base(m)).length > 1
      const items = meshes.map((m) => {
        const parts = [ctx.partFor(m)]
        // Two guides of one kind would otherwise both read "Mandible Resection
        // Guide"; the word that tells them apart is added.
        const q = shared(m) ? qualifierOf(m.name) : null
        const label = q ? `${base(m)} - ${q}` : base(m)
        const sceneId = ctx.addScene(label, parts, undefined, ctx.impliedFrame(parts))
        return { sceneId, view: 'ISO', caption: label, annotations: [] }
      })
      // As delivered: ORTHO p15 puts five print models on ONE page in two columns; FIBULA
      // p20/p21 put two on a page, stacked. So up to six per page — one column for one or two
      // models, two columns from three up. Three per page, stacked, sent a fourth model to a
      // page of its own (Deepak, 28 Sep: "All should be on a single page all 4").
      const { perPage, cols } = galleryPaging(items.length)
      for (let i = 0; i < items.length; i += perPage) {
        const page = newGalleryPage(ctx.nextId('p'), { items: items.slice(i, i + perPage), cols })
        page.captions.tc = i === 0 ? 'Print models' : 'Print models (cont.)'
        page.recipeId = i === 0 ? ctx.recipe.id : `${ctx.recipe.id}#${i / perPage}`
        ctx.doc.pages.push(page)
      }
    },
  },
  {
    // The CT block comes from the portal by Case ID — the lookup the Builder
    // already makes. Titled from the scan's own region ("Head data", "Lower
    // extremity data"), never a fixed "Skull data" that once printed over a
    // foot scan.
    //
    // The mesh is OPTIONAL. The master's own page is text in both columns, so a
    // page with no mesh chosen is complete and says nothing; a chosen mesh is
    // rendered into the right column, which is where the orbital report's page 1
    // has always put its isometric skull. Team, 5 Oct 2026: "There is no option
    // to add OBJ files for generating images on the Preview Image page."
    id: 'g-ct-data', title: 'CT data', kind: 'custom', block: 'info', repeatable: false,
    inputs: ['meshes'],
    compose(ctx) {
      const rows = CT_FIELDS
        .filter(([, , o]) => o && o.core)
        .map(([label, key]) => [label, ctx.ctInfo?.[key]])
        .filter(([, v]) => v != null && v !== '')
      if (!rows.length) {
        note(ctx, 'skipped', 'not built — no scan data yet. It is read from the portal by Case ID; '
          + 'enter the Case ID, or type the fields in the CT data panel.')
        return
      }
      // One picture, however many meshes are picked — they compose into a single
      // scene, as every other page of this file does, so a skull plus a plate is
      // one view of the two and not two half-size views.
      const { found, gone } = chosenMeshes(ctx)
      if (gone) {
        note(ctx, 'incomplete',
          `built WITHOUT ${gone} chosen mesh${gone === 1 ? '' : 'es'} no longer in the case `
          + 'folder — choose again')
      }
      const images = []
      if (found.length) {
        const parts = found.map(ctx.partFor)
        images.push({
          kind: 'scene',
          sceneId: ctx.addScene('Preview image', parts, undefined, ctx.impliedFrame(parts)),
          view: 'ISO',
        })
      }
      const page = newInfoPage(ctx.nextId('p'), {
        groups: [{ title: ctSubheading(ctx.ctInfo), rows }],
        images,
      })
      page.title = 'CT data information'
      // The quality waiver, in the slot the master already reserves for it
      // (template.js sourceData.waiver, x 9.23 baseline 203.88) and drawn by the
      // same sourceDataOps every format uses. One builder, shared with the orbital
      // recipe and the orthognathic source-data page.
      //
      // The judgement stays the operator's. ctAutofill.waiverSuggested only hints,
      // on a study more than 30 days old, and never ticks the box.
      const wv = waiverBlockFor(ctx.waiver)
      if (wv) page.waiverBlock = wv
      page.recipeId = ctx.recipe.id
      ctx.doc.pages.push(page)
    },
  },
  {
    // The colour map — ORTHO p6/p7 teeth-interference thickness, FIBULA p19 mandible
    // thickness. The render is the team's ZBrush image, supplied as this page's image;
    // the legend is typed. The Builder lays the page out around it, which is the
    // house template's `image` split: the engine cannot make the map, and need not.
    id: 'g-colour-map', title: 'Colour map', kind: 'custom', block: 'gallery', repeatable: true,
    inputs: ['image', 'legend'],
    compose(ctx) {
      const image = ctx.entry?.inputs?.image
      if (!image) {
        note(ctx, 'skipped', 'not built — give this page its colour-map image (the ZBrush render)')
        return
      }
      const legend = parseLegend(ctx.entry?.inputs?.legend)
      const page = newGalleryPage(ctx.nextId('p'), {
        items: [{ sceneId: null, view: 'A', override: image, caption: '', annotations: [] }],
        cols: 1,
      })
      page.legend = legend
      page.captions = { ...page.captions, ...NO_CAPTIONS, ...(ctx.recipe.captions ?? {}) }
      page.recipeId = ctx.recipe.id
      ctx.doc.pages.push(page)
    },
  },
  text('g-agreement', 'Agreement', AGREEMENT_BODY, AGREEMENT_STYLES),
  text('g-terms-1', 'Terms and Conditions (1 of 2)', TERMS_BODY_1, TERMS_STYLES),
  text('g-terms-2', 'Terms and Conditions (2 of 2)', TERMS_BODY_2, TERMS_STYLES),
  // The Titanium PSI report's own legal pages. Its template carries DIFFERENT wording, not just
  // the product name (boilerplate.js records every difference, and boilerplateTiPsi.test.js
  // checks the text against the template itself), so it cannot share the three above.
  //
  // There is no `g-ti-terms-2`: the template's page 16 is a raster, so Ti PSI reuses the house
  // TERMS_BODY_2 through the existing `g-terms-2` rather than carrying a second copy of it.
  text('g-ti-agreement', 'Agreement', TI_PSI_AGREEMENT_BODY, AGREEMENT_STYLES, { formatOnly: true }),
  text('g-ti-terms-1', 'Terms and Conditions', TI_PSI_TERMS_BODY_1, TERMS_STYLES, { formatOnly: true }),
]

/**
 * Page-type ids that have been renamed, old → new. A basket saved under the old
 * one still resolves.
 *
 * `g-four-views` was this page's id for one afternoon on 1 Oct 2026, before the
 * fibula template's three-view leg pages showed it is a row of ANY length.
 *
 * Exported because BOTH lookups have to honour it: the Builder's own list through
 * `genericPage`, and the draft's `recipeById`. Honouring it in one of the two put
 * the page in the Builder and left it out of the report.
 */
export const RENAMED_PAGE_TYPES = { 'g-four-views': 'g-view-row' }

export const genericPage = (id) => GENERIC_PAGES.find(
  (t) => t.id === (RENAMED_PAGE_TYPES[id] ?? id),
)
